import { useEffect, useMemo, useState } from 'react';
import { getLocalProfile } from '../lib/localProfile';
import { getMedalLedger } from '../lib/medals';
import {
  bestMedalIcon,
  buildAchievements,
  medalCount,
  personalHeadline,
} from '../lib/personalStats';
import { getNewsUnreadCount, NEWS_SEEN_KEY } from '../lib/news';
import { PageSeo } from './PageSeo';
import { BackToToday, PageHeader } from './SiteChrome';
import { ContentLayout } from './PageSidebar';
import { SiteFooter } from './SiteFooter';
import { PAGE_META } from '../lib/seo';

const BRAND = 'linear-gradient(to right, #CD7F32, #a1a1aa, #facc15, #22d3ee)';

export function YouPage({ userId }: { userId: string }) {
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);

  const profile = useMemo(() => getLocalProfile(userId), [userId]);
  const medals = useMemo(() => getMedalLedger(userId), [userId]);
  const headline = personalHeadline(profile, medals);
  const achievements = buildAchievements(profile, medals);
  const unlocked = achievements.filter((a) => a.unlocked).length;
  const totalMedals = medalCount(medals);
  const bestIcon = bestMedalIcon(profile.high_score);

  useEffect(() => {
    try {
      setNewsUnreadCount(getNewsUnreadCount(localStorage.getItem(NEWS_SEEN_KEY)));
    } catch {
      setNewsUnreadCount(0);
    }
  }, []);

  const lastPlayed = profile.last_played_date
    ? new Date(`${profile.last_played_date}T12:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="relative pb-8">
      <PageSeo meta={PAGE_META.you} />
      <PageHeader newsUnreadCount={newsUnreadCount} />

      <ContentLayout current="you">
      <BackToToday />
      <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-none mb-3">You</h1>
      <p className="text-sm font-semibold leading-snug mb-1">{headline.headline}</p>
      <p className="text-sm text-foreground/55 leading-relaxed mb-8">{headline.sub}</p>

      <div className="grid grid-cols-2 gap-3 mb-8">
        <StatTile label="Games" value={String(profile.games_played)} />
        <StatTile
          label="Best"
          value={String(profile.high_score)}
          hint={bestIcon ? `${bestIcon} personal record` : 'out of 490'}
        />
        <StatTile
          label="Streak"
          value={String(profile.current_streak)}
          hint={profile.current_streak > 0 ? 'days in a row' : 'play today to start one'}
          accent={profile.current_streak >= 3}
        />
        <StatTile label="Best streak" value={String(profile.max_streak)} hint="personal record" />
      </div>

      <section className="mb-8">
        <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-3">Medal cabinet</div>
        <div className="grid grid-cols-2 gap-3">
          <MedalCard emoji="🥉" name="Bronze" count={medals.bronze} hint="50+ points" />
          <MedalCard emoji="🥈" name="Silver" count={medals.silver} hint="150+ points" />
          <MedalCard emoji="🥇" name="Gold" count={medals.gold} hint="300+ points" />
          <MedalCard emoji="💎" name="Platinum" count={medals.platinum} hint="A perfect 490" featured />
        </div>
        <p className="mt-3 text-xs text-foreground/45">
          {totalMedals === 0
            ? 'No medals yet. 50 points gets you on the board.'
            : `${totalMedals} medal${totalMedals === 1 ? '' : 's'} earned${lastPlayed ? ` · last played ${lastPlayed}` : ''}.`}
        </p>
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">Achievements</div>
          <div className="text-[11px] font-bold tabular-nums text-foreground/40">
            {unlocked}/{achievements.length}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {achievements.map((a) => (
            <div
              key={a.id}
              className={`rounded-2xl border px-4 py-3 flex items-start gap-3 ${
                a.unlocked ? 'border-border bg-muted/40' : 'border-border/60 bg-transparent opacity-45'
              }`}
            >
              <div className="text-xl leading-none mt-0.5">{a.emoji}</div>
              <div className="min-w-0">
                <div className="text-sm font-bold">{a.title}</div>
                <div className="text-xs text-foreground/50 mt-0.5">{a.blurb}</div>
              </div>
              {a.unlocked && (
                <div
                  className="ml-auto mt-1 h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundImage: BRAND }}
                  title="Unlocked"
                />
              )}
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
      </ContentLayout>
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3 relative overflow-hidden">
      {accent && (
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-0.5"
          style={{ backgroundImage: BRAND }}
        />
      )}
      <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">{label}</div>
      <div className="text-3xl font-black tabular-nums mt-1 tracking-tight">{value}</div>
      {hint && <div className="text-xs text-foreground/45 mt-0.5">{hint}</div>}
    </div>
  );
}

function MedalCard({
  emoji,
  name,
  count,
  hint,
  featured,
}: {
  emoji: string;
  name: string;
  count: number;
  hint: string;
  featured?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border px-4 py-3 ${
        featured && count > 0
          ? 'border-transparent'
          : 'border-border bg-muted/40'
      }`}
      style={
        featured && count > 0
          ? {
              backgroundImage: `linear-gradient(var(--background), var(--background)), ${BRAND}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }
          : undefined
      }
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">{name}</div>
        <div className="text-xl">{emoji}</div>
      </div>
      <div className="text-3xl font-black tabular-nums mt-1">{count}</div>
      <div className="text-xs text-foreground/45 mt-0.5">{hint}</div>
    </div>
  );
}
